export interface Question {
  id: string
  questionNumber: string
  questionText: string
  responseText: string
  tags: string[]
}

export interface DiscoverySet {
  id: string
  setName: string
  type: 'Interrogatory' | 'RFP' | 'RFA'
  from: string
  to: string
  dateServed: string
  dateResponded: string | null
  questionCount: number
  status: 'Complete' | 'Partial' | 'Pending' | 'Overdue'
  sourcePdf: string
  questions: Question[]
}

export interface CaseDetails {
  id: string
  name: string
  number: string
  court: string
  judge: string
  parties: {
    role: string
    name: string
    counsel: string
  }[]
}

export interface CaseSummary {
  id: string
  name: string
  number: string
  court: string
  documentCount: number
  discoverySets: number
  parties: number
  status: 'Active' | 'Pending' | 'Closed'
  lastAccessed: string
}

export const mockCase: CaseDetails = {
  id: 'case-001',
  name: 'Meridian Properties LLC v. Blackstone Development Corp., et al.',
  number: '2024-CV-04892',
  court: 'Superior Court, County of Fulton',
  judge: 'Hon. Patricia R. Whitfield',
  parties: [
    {
      role: 'Plaintiff',
      name: 'Meridian Properties LLC',
      counsel: 'Sarah Chen',
    },
    {
      role: 'Defendant 1',
      name: 'Blackstone Development Corp.',
      counsel: 'James Harwick',
    },
    {
      role: 'Defendant 2',
      name: 'Greenfield Construction Inc.',
      counsel: 'Maria Santos',
    },
    {
      role: 'Defendant 3',
      name: 'Atlas Engineering Group',
      counsel: 'Robert Tillman',
    },
  ],
}

export const mockCases: CaseSummary[] = [
  {
    id: 'case-001',
    name: 'Meridian Properties v. Blackstone Dev., et al.',
    number: '2024-CV-04892',
    court: 'Superior Court, Fulton County',
    documentCount: 3847,
    discoverySets: 5,
    parties: 4,
    status: 'Active',
    lastAccessed: 'Mar 6, 2026',
  },
  {
    id: 'case-002',
    name: 'In re: TechHoldings Merger Litigation',
    number: '2024-CV-01102',
    court: 'Court of Chancery, Delaware',
    documentCount: 14502,
    discoverySets: 12,
    parties: 7,
    status: 'Active',
    lastAccessed: 'Mar 4, 2026',
  },
  {
    id: 'case-003',
    name: 'State of NY v. Horizon Logistics Corp.',
    number: '2025-CV-00318',
    court: 'Supreme Court, New York County',
    documentCount: 892,
    discoverySets: 3,
    parties: 2,
    status: 'Active',
    lastAccessed: 'Feb 28, 2026',
  },
  {
    id: 'case-004',
    name: 'Calloway Estate v. First National Trust',
    number: '2023-PR-07741',
    court: 'Probate Court, Cobb County',
    documentCount: 1204,
    discoverySets: 6,
    parties: 5,
    status: 'Pending',
    lastAccessed: 'Jan 18, 2026',
  },
]

export const mockDiscoverySets: DiscoverySet[] = [
  {
    id: 'set-001',
    setName: "Plaintiff's First Set of Interrogatories to Blackstone",
    type: 'Interrogatory',
    from: 'Meridian Properties',
    to: 'Blackstone Dev.',
    dateServed: '2024-01-15',
    dateResponded: '2024-02-14',
    questionCount: 12,
    status: 'Complete',
    sourcePdf: 'PL_ROG_1_BLK.pdf',
    questions: [
      {
        id: 'q-001-1',
        questionNumber: 'ROG 1',
        questionText:
          'Identify all persons who participated in the drafting, review, or approval of the construction contract between Meridian Properties LLC and Blackstone Development Corp. dated March 15, 2022, and for each such person, state their role, title, employer, the dates of their involvement, and the nature and extent of their participation.',
        responseText:
          "Objection. This interrogatory is overly broad, unduly burdensome, and seeks information protected by the attorney-client privilege and the work product doctrine. The interrogatory is compound in that it seeks multiple categories of information for each individual, effectively constituting multiple interrogatories in violation of Rule 33. It further seeks information regarding legal counsel's involvement in contract review, which is protected from disclosure.\n\nSubject to and without waiving said objections, and interpreting this interrogatory to seek only non-privileged information regarding non-attorney personnel who participated in the substantive business review of the contract, Defendant states as follows:\n\n1. John Smith, Vice President of Construction, Blackstone Development Corp. Mr. Smith was involved from approximately January 2022 through March 15, 2022. His participation included reviewing the scope of work provisions (Sections 3.1 through 3.14), the project timeline and milestone schedule (Exhibit B), the warranty provisions (Section 12), and the liquidated damages clause (Section 15.3). Mr. Smith provided comments on three successive drafts dated January 22, 2022, February 8, 2022, and February 28, 2022. Mr. Smith also participated in two in-person negotiation sessions with Meridian Properties representatives on February 12, 2022 and March 4, 2022.\n\n2. Mary Johnson, General Counsel, Blackstone Development Corp. To the extent this interrogatory seeks information about Ms. Johnson's legal review, Defendant objects on the basis of attorney-client privilege. Defendant will state that Ms. Johnson reviewed the final draft in her capacity as a corporate officer authorized to bind the company.\n\n3. Thomas Reed, Project Manager, Blackstone Development Corp. Mr. Reed was consulted regarding the technical specifications in Exhibit A and the construction schedule in Exhibit B during February 2022. Mr. Reed provided written comments to Mr. Smith via email on February 15, 2022, regarding the feasibility of the proposed foundation timeline. These communications have been produced at Bates numbers BLK_003201 through BLK_003215.\n\n4. Diana Chen, Chief Financial Officer, Blackstone Development Corp. Ms. Chen reviewed the payment terms (Section 8), the retainage provisions (Section 8.4), the insurance requirements (Section 11), and the performance bond provisions (Section 10) during the period of February 20, 2022 through March 10, 2022. Ms. Chen's review focused on the financial exposure and cash flow implications of the milestone payment structure.\n\n5. Robert Williams, Senior Estimator, Blackstone Development Corp. Mr. Williams prepared the initial cost estimate that formed the basis for the contract price and reviewed the final contract price against his estimates. His involvement was primarily during January 2022.\n\nDefendant reserves the right to supplement this response as additional information becomes available through ongoing document review and investigation.",
        tags: ['Objection', 'Privilege', 'Substantive', 'Amended'],
      },
      {
        id: 'q-001-2',
        questionNumber: 'ROG 2',
        questionText:
          'Describe in detail all communications between Blackstone Development Corp. and Greenfield Construction Inc. regarding the foundation pouring at the subject property between June 1, 2022, and July 31, 2022.',
        responseText:
          "Objection. Vague and ambiguous as to 'communications'. Subject to said objection, Defendant refers Plaintiff to the daily site logs and email correspondence produced concurrently herewith at BLK_001452 through BLK_002105.",
        tags: ['Objection', 'Refers to Documents', 'Evasive'],
      },
      {
        id: 'q-001-3',
        questionNumber: 'ROG 3',
        questionText:
          'State the date on which Blackstone Development Corp. first became aware of water intrusion in the basement level of the subject property.',
        responseText:
          "Defendant first received notice of potential water intrusion via an email from Plaintiff's property manager on October 12, 2023.",
        tags: ['Substantive'],
      },
    ],
  },
  {
    id: 'set-002',
    setName: "Plaintiff's First Requests for Production to Greenfield",
    type: 'RFP',
    from: 'Meridian Properties',
    to: 'Greenfield Const.',
    dateServed: '2024-01-20',
    dateResponded: '2024-03-01',
    questionCount: 15,
    status: 'Partial',
    sourcePdf: 'PL_RFP_1_GRN.pdf',
    questions: [
      {
        id: 'q-002-1',
        questionNumber: 'RFP 1',
        questionText:
          'All daily logs, site reports, and inspection reports relating to the foundation work performed at the subject property.',
        responseText:
          'Defendant will produce non-privileged documents responsive to this request located after a reasonable search. See documents Bates labeled GRN_000001 to GRN_000850.',
        tags: ['Substantive', 'Refers to Documents'],
      },
      {
        id: 'q-002-2',
        questionNumber: 'RFP 2',
        questionText:
          'All communications, including emails and text messages, between your employees and Atlas Engineering Group regarding soil compaction testing.',
        responseText:
          'Objection. Overly broad and unduly burdensome. Defendant is continuing to search for responsive documents and will supplement this response.',
        tags: ['Objection', 'Partial'],
      },
    ],
  },
  {
    id: 'set-003',
    setName: "Blackstone's First Set of Interrogatories to Plaintiff",
    type: 'Interrogatory',
    from: 'Blackstone Dev.',
    to: 'Meridian Properties',
    dateServed: '2024-02-05',
    dateResponded: '2024-03-07',
    questionCount: 10,
    status: 'Complete',
    sourcePdf: 'BLK_ROG_1_PL.pdf',
    questions: [
      {
        id: 'q-003-1',
        questionNumber: 'ROG 1',
        questionText:
          'Itemize all damages Plaintiff claims to have suffered as a result of the alleged construction defects, including the method of calculation.',
        responseText:
          'Plaintiff objects to this interrogatory to the extent it calls for expert testimony prior to the deadline for expert disclosures. Subject to that objection, Plaintiff has incurred $450,000 in remediation costs to date, as detailed in the invoices produced at MER_00100 to MER_00250. Plaintiff anticipates an additional $1.2 million in future repair costs based on preliminary estimates.',
        tags: ['Objection', 'Substantive', 'Refers to Documents'],
      },
    ],
  },
  {
    id: 'set-004',
    setName: "Plaintiff's Requests for Admission to Atlas",
    type: 'RFA',
    from: 'Meridian Properties',
    to: 'Atlas Engineering',
    dateServed: '2024-03-10',
    dateResponded: null,
    questionCount: 20,
    status: 'Overdue',
    sourcePdf: 'PL_RFA_1_ATL.pdf',
    questions: [],
  },
  {
    id: 'set-005',
    setName: "Greenfield's Requests for Production to Plaintiff",
    type: 'RFP',
    from: 'Greenfield Const.',
    to: 'Meridian Properties',
    dateServed: '2024-04-01',
    dateResponded: null,
    questionCount: 8,
    status: 'Pending',
    sourcePdf: 'GRN_RFP_1_PL.pdf',
    questions: [],
  },
]
